import React, { useState } from 'react';
import {
  Avatar,
  Badge,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { PhotoCamera as CameraIcon } from '@mui/icons-material';
import { uploadDriverProfilePicture } from '../../utils/fileUploadService';

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    width: 32,
    height: 32,
    borderRadius: '50%',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}));

const HiddenInput = styled('input')({
  display: 'none',
});

/**
 * Avatar-Komponente mit Upload-Funktion
 * @param {Object} props - Komponenten-Properties
 * @param {string} props.src - Aktuelle Bild-URL
 * @param {string} props.alt - Alternativer Text für das Bild
 * @param {function} props.onUploadComplete - Callback, der nach erfolgreichem Upload aufgerufen wird
 * @param {string} props.userId - ID des Benutzers (für Storage-Pfad)
 * @param {Object} props.sx - Zusätzliche Styling-Properties für Avatar
 */
const AvatarUpload = ({ src, alt, onUploadComplete, userId, sx = {} }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      
      // Bild hochladen und Download-URL erhalten
      const downloadURL = await uploadDriverProfilePicture(
        file, 
        userId,
        (progress) => setUploadProgress(progress)
      );

      // Callback mit der Download-URL aufrufen
      if (onUploadComplete) {
        onUploadComplete(downloadURL);
      }
    } catch (error) {
      console.error('Fehler beim Hochladen des Profilbilds:', error);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <StyledBadge
      overlap="circular"
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      badgeContent={
        uploading ? (
          <CircularProgress 
            size={32}
            variant="determinate"
            value={uploadProgress} 
            color="primary"
          />
        ) : (
          <Tooltip title="Profilbild ändern">
            <IconButton 
              component="label" 
              size="small"
              htmlFor="avatar-upload"
            >
              <CameraIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )
      }
    >
      <Avatar 
        src={src} 
        alt={alt}
        sx={{ 
          width: 120, 
          height: 120,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          fontSize: '3rem',
          ...sx
        }}
      >
        {alt ? alt.charAt(0).toUpperCase() : 'U'}
      </Avatar>
      <HiddenInput
        id="avatar-upload"
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        disabled={uploading}
      />
    </StyledBadge>
  );
};

export default AvatarUpload;